import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ChevronDown, Plus, Trash2, Save, Wrench, MapPin, FileText, Shield, Ship, Layers } from "lucide-react";
import { toast } from "sonner";

type Vessel = { id: string; name: string; vessel_type?: string | null };

type Component = {
  id: string;
  vessel_id: string;
  vessel_name: string;
  parent_equipment_id: string | null;
  node_level: string;
  equipment_name: string;
  equipment_code: string | null;
  category: string;
  system_name: string | null;
  sub_system: string | null;
  manufacturer: string | null;
  manufacturer_country: string | null;
  manufacturer_contact: string | null;
  model: string | null;
  serial_number: string | null;
  install_date: string | null;
  commissioned_date: string | null;
  warranty_expiry: string | null;
  expected_life_years: number | null;
  criticality: string;
  technical_specs: string | null;
  // location
  frame_from: string | null;
  frame_to: string | null;
  side: string | null;
  deck_level: string | null;
  compartment: string | null;
  inner_outer: string | null;
  location_on_vessel: string | null;
  // drawing
  ga_drawing_no: string | null;
  pid_tag: string | null;
  equipment_tag: string | null;
  // 3D
  pos_x: number | null;
  pos_y: number | null;
  pos_z: number | null;
  notes: string | null;
};

type Job = {
  id: string;
  job_number: string;
  equipment_id: string;
  vessel_id: string;
  vessel_name: string;
  title: string;
  description: string | null;
  job_type: string;
  interval_hours: number | null;
  interval_days: number | null;
  priority: string;
  department: string;
  estimated_duration_hours: number | null;
  procedures: string | null;
  procedure_steps: any;
  tools_required: string | null;
  spares_required: string | null;
  ppe_required: string | null;
  isolations_loto: string | null;
  permits_required: string | null;
  required_rank: string | null;
  criticality: string | null;
  class_requirement: boolean;
  class_society: string | null;
  cms_credit: boolean;
  regulatory_reference: string | null;
  acceptance_criteria: string | null;
  signoff_chain: string | null;
};

const NODE_LEVELS = [
  { value: "system", label: "System" },
  { value: "sub_system", label: "Sub-system" },
  { value: "equipment", label: "Equipment" },
  { value: "component", label: "Component" },
  { value: "sub_component", label: "Sub-component" },
];

const SIDES = ["port", "stbd", "centerline"];
const INNER_OUTER = ["inner", "outer", "skin"];
const CRITICALITIES = ["A", "B", "C"];

const emptyComponent = (vessel: Vessel, parent: Component | null): Partial<Component> => ({
  vessel_id: vessel.id,
  vessel_name: vessel.name,
  parent_equipment_id: parent?.id ?? null,
  node_level: parent ? nextLevel(parent.node_level) : "system",
  equipment_name: "",
  category: "general",
  criticality: "B",
});

function nextLevel(current: string) {
  const idx = NODE_LEVELS.findIndex((l) => l.value === current);
  return NODE_LEVELS[Math.min(idx + 1, NODE_LEVELS.length - 1)].value;
}

const emptyJob = (component: Component): Partial<Job> => ({
  equipment_id: component.id,
  vessel_id: component.vessel_id,
  vessel_name: component.vessel_name,
  title: "",
  job_type: "calendar",
  interval_days: 30,
  priority: "medium",
  department: "engine",
  estimated_duration_hours: 1,
  criticality: component.criticality,
  procedure_steps: [],
});

export default function AdminPMSBuilder() {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselId, setVesselId] = useState<string>("");
  const [components, setComponents] = useState<Component[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Component> | null>(null);
  const [editingJob, setEditingJob] = useState<Partial<Job> | null>(null);
  const [loading, setLoading] = useState(false);

  const vessel = vessels.find((v) => v.id === vesselId);
  const selected = components.find((c) => c.id === selectedId) || null;
  const selectedJobs = jobs.filter((j) => j.equipment_id === selectedId);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("fleet_vessels").select("id,name,vessel_type").order("name");
      setVessels(data || []);
      if (data?.[0] && !vesselId) setVesselId(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!vesselId) return;
    loadTree();
  }, [vesselId]);

  async function loadTree() {
    setLoading(true);
    const [{ data: comps }, { data: js }] = await Promise.all([
      supabase.from("pms_equipment").select("*").eq("vessel_id", vesselId).order("node_level").order("equipment_name"),
      supabase.from("pms_jobs").select("*").eq("vessel_id", vesselId).order("title"),
    ]);
    setComponents((comps as Component[]) || []);
    setJobs((js as Job[]) || []);
    setLoading(false);
  }

  const tree = useMemo(() => {
    const byParent = new Map<string | null, Component[]>();
    components.forEach((c) => {
      const k = c.parent_equipment_id;
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k)!.push(c);
    });
    return byParent;
  }, [components]);

  function toggle(id: string) {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  }

  function pickComponent(c: Component) {
    setSelectedId(c.id);
    setEditing(c);
    setEditingJob(null);
  }

  function startNew(parent: Component | null) {
    if (!vessel) return;
    setSelectedId(null);
    setEditing(emptyComponent(vessel, parent));
    setEditingJob(null);
  }

  async function saveComponent() {
    if (!editing?.equipment_name || !editing?.vessel_id) {
      toast.error("Component name required");
      return;
    }
    const payload: any = { ...editing };
    if (payload.id) {
      const { error } = await supabase.from("pms_equipment").update(payload).eq("id", payload.id);
      if (error) return toast.error(error.message);
      toast.success("Component updated");
    } else {
      const { data, error } = await supabase.from("pms_equipment").insert(payload).select().single();
      if (error) return toast.error(error.message);
      toast.success("Component added");
      setSelectedId(data.id);
      if (payload.parent_equipment_id) {
        const n = new Set(expanded);
        n.add(payload.parent_equipment_id);
        setExpanded(n);
      }
    }
    await loadTree();
  }

  async function deleteComponent(c: Component) {
    if (!confirm(`Delete "${c.equipment_name}" and all its children + jobs?`)) return;
    const { error } = await supabase.from("pms_equipment").delete().eq("id", c.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    if (selectedId === c.id) {
      setSelectedId(null);
      setEditing(null);
    }
    await loadTree();
  }

  async function saveJob() {
    if (!editingJob?.title || !editingJob?.equipment_id) {
      toast.error("Job title required");
      return;
    }
    const payload: any = { ...editingJob };
    if (payload.id) {
      const { error } = await supabase.from("pms_jobs").update(payload).eq("id", payload.id);
      if (error) return toast.error(error.message);
      toast.success("Job saved");
    } else {
      const { error } = await supabase.from("pms_jobs").insert(payload);
      if (error) return toast.error(error.message);
      toast.success("Job created");
    }
    setEditingJob(null);
    await loadTree();
  }

  async function deleteJob(j: Job) {
    if (!confirm(`Delete job "${j.title}"?`)) return;
    const { error } = await supabase.from("pms_jobs").delete().eq("id", j.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    await loadTree();
  }

  function renderNode(c: Component, depth: number) {
    const children = tree.get(c.id) || [];
    const isOpen = expanded.has(c.id);
    const isSel = selectedId === c.id;
    const jobCount = jobs.filter((j) => j.equipment_id === c.id).length;
    return (
      <div key={c.id}>
        <div
          className={`flex items-center gap-1 py-1.5 pr-2 rounded cursor-pointer hover:bg-primary-foreground/5 ${
            isSel ? "bg-teal/10 border-l-2 border-teal" : "border-l-2 border-transparent"
          }`}
          style={{ paddingLeft: depth * 14 + 6 }}
          onClick={() => pickComponent(c)}
        >
          {children.length > 0 ? (
            <button
              className="p-0.5 text-primary-foreground/50 hover:text-primary-foreground"
              onClick={(e) => {
                e.stopPropagation();
                toggle(c.id);
              }}
            >
              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <span className="text-[10px] uppercase tracking-wider text-teal/60 font-mono w-16 shrink-0">
            {c.node_level.replace("_", "-")}
          </span>
          <span className="text-sm text-primary-foreground/90 truncate flex-1">{c.equipment_name}</span>
          {c.criticality === "A" && <Badge variant="destructive" className="h-4 text-[9px] px-1">A</Badge>}
          {jobCount > 0 && (
            <span className="text-[10px] text-primary-foreground/40 font-mono flex items-center gap-0.5">
              <Wrench className="w-2.5 h-2.5" />
              {jobCount}
            </span>
          )}
          <button
            className="p-0.5 opacity-0 group-hover:opacity-100 text-primary-foreground/40 hover:text-teal"
            title="Add child"
            onClick={(e) => {
              e.stopPropagation();
              startNew(c);
            }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        {isOpen && children.map((ch) => renderNode(ch, depth + 1))}
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary-foreground font-display flex items-center gap-2">
          <Layers className="w-6 h-6 text-teal" /> PMS Component Tree Builder
        </h1>
        <p className="text-sm text-primary-foreground/60 mt-1">
          Define vessel hierarchy, location, manufacturer details, and recommended maintenance procedures. Crew use this as their authoritative guidance onboard.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Ship className="w-4 h-4 text-teal" />
        <Select value={vesselId} onValueChange={setVesselId}>
          <SelectTrigger className="w-[320px] bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground">
            <SelectValue placeholder="Select vessel" />
          </SelectTrigger>
          <SelectContent>
            {vessels.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.name} {v.vessel_type ? `· ${v.vessel_type}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => startNew(null)} disabled={!vessel}>
          <Plus className="w-4 h-4 mr-1" /> Add Top-level System
        </Button>
        <span className="text-xs text-primary-foreground/40 ml-auto">
          {components.length} nodes · {jobs.length} jobs
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Tree */}
        <Card className="lg:col-span-4 p-3 bg-navy-deep/50 border-primary-foreground/10 max-h-[78vh] overflow-auto">
          {loading ? (
            <p className="text-xs text-primary-foreground/40 p-4">Loading…</p>
          ) : (tree.get(null) || []).length === 0 ? (
            <p className="text-xs text-primary-foreground/40 p-4">
              No components yet. Click <em>Add Top-level System</em> to begin.
            </p>
          ) : (
            <div className="group">{(tree.get(null) || []).map((c) => renderNode(c, 0))}</div>
          )}
        </Card>

        {/* Editor */}
        <Card className="lg:col-span-8 p-5 bg-navy-deep/50 border-primary-foreground/10 max-h-[78vh] overflow-auto">
          {!editing && !editingJob && (
            <div className="text-center py-16 text-primary-foreground/40 text-sm">
              Select a node from the tree, or add a new one to begin editing.
            </div>
          )}

          {editing && !editingJob && (
            <ComponentEditor
              editing={editing}
              setEditing={setEditing}
              onSave={saveComponent}
              onDelete={selected ? () => deleteComponent(selected as Component) : null}
              jobs={selectedJobs}
              onAddJob={() => selected && setEditingJob(emptyJob(selected as Component))}
              onEditJob={(j) => setEditingJob(j)}
              onDeleteJob={deleteJob}
              isNew={!editing.id}
            />
          )}

          {editingJob && (
            <JobEditor
              editing={editingJob}
              setEditing={setEditingJob}
              onSave={saveJob}
              onCancel={() => setEditingJob(null)}
            />
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}

function field(label: string, child: React.ReactNode, hint?: string) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-primary-foreground/60 uppercase tracking-wide">{label}</Label>
      {child}
      {hint && <p className="text-[10px] text-primary-foreground/30">{hint}</p>}
    </div>
  );
}

function ComponentEditor({
  editing,
  setEditing,
  onSave,
  onDelete,
  jobs,
  onAddJob,
  onEditJob,
  onDeleteJob,
  isNew,
}: {
  editing: Partial<Component>;
  setEditing: (c: Partial<Component>) => void;
  onSave: () => void;
  onDelete: (() => void) | null;
  jobs: Job[];
  onAddJob: () => void;
  onEditJob: (j: Job) => void;
  onDeleteJob: (j: Job) => void;
  isNew: boolean;
}) {
  const set = (k: keyof Component, v: any) => setEditing({ ...editing, [k]: v });

  const inputCls = "bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-primary-foreground/10 pb-3">
        <div>
          <h2 className="text-lg font-semibold text-primary-foreground">
            {isNew ? "New Component" : editing.equipment_name}
          </h2>
          <p className="text-xs text-primary-foreground/40">
            {NODE_LEVELS.find((l) => l.value === editing.node_level)?.label} · {editing.vessel_name}
          </p>
        </div>
        <div className="flex gap-2">
          {!isNew && onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button size="sm" onClick={onSave}>
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="basics">
        <TabsList className="bg-primary-foreground/5">
          <TabsTrigger value="basics"><Layers className="w-3.5 h-3.5 mr-1" />Basics</TabsTrigger>
          <TabsTrigger value="location"><MapPin className="w-3.5 h-3.5 mr-1" />Location</TabsTrigger>
          <TabsTrigger value="drawings"><FileText className="w-3.5 h-3.5 mr-1" />Drawings & Tags</TabsTrigger>
          <TabsTrigger value="manufacturer"><Shield className="w-3.5 h-3.5 mr-1" />Manufacturer</TabsTrigger>
          <TabsTrigger value="jobs" disabled={isNew}>
            <Wrench className="w-3.5 h-3.5 mr-1" />Maintenance ({jobs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="grid grid-cols-2 gap-4 pt-4">
          {field("Hierarchy Level",
            <Select value={editing.node_level || "equipment"} onValueChange={(v) => set("node_level", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {NODE_LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {field("Criticality",
            <Select value={editing.criticality || "B"} onValueChange={(v) => set("criticality", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CRITICALITIES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>,
            "A = critical, B = important, C = routine"
          )}
          {field("Name *", <Input className={inputCls} value={editing.equipment_name || ""} onChange={(e) => set("equipment_name", e.target.value)} />)}
          {field("Code / Tag", <Input className={inputCls} value={editing.equipment_code || ""} onChange={(e) => set("equipment_code", e.target.value)} />)}
          {field("System", <Input className={inputCls} value={editing.system_name || ""} onChange={(e) => set("system_name", e.target.value)} placeholder="e.g. Propulsion" />)}
          {field("Sub-system", <Input className={inputCls} value={editing.sub_system || ""} onChange={(e) => set("sub_system", e.target.value)} placeholder="e.g. Lube Oil" />)}
          {field("Category", <Input className={inputCls} value={editing.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="engine, deck, electrical…" />)}
          {field("Quantity", <Input type="number" className={inputCls} value={(editing as any).quantity ?? 1} onChange={(e) => set("quantity" as any, Number(e.target.value))} />)}
          <div className="col-span-2">
            {field("Technical Specifications", <Textarea className={inputCls} rows={3} value={editing.technical_specs || ""} onChange={(e) => set("technical_specs", e.target.value)} />)}
          </div>
          <div className="col-span-2">
            {field("Notes", <Textarea className={inputCls} rows={2} value={editing.notes || ""} onChange={(e) => set("notes", e.target.value)} />)}
          </div>
        </TabsContent>

        <TabsContent value="location" className="grid grid-cols-3 gap-4 pt-4">
          {field("Frame From", <Input className={inputCls} value={editing.frame_from || ""} onChange={(e) => set("frame_from", e.target.value)} placeholder="e.g. FR 42" />)}
          {field("Frame To", <Input className={inputCls} value={editing.frame_to || ""} onChange={(e) => set("frame_to", e.target.value)} placeholder="e.g. FR 48" />)}
          {field("Side",
            <Select value={editing.side || ""} onValueChange={(v) => set("side", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{SIDES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {field("Deck / Level", <Input className={inputCls} value={editing.deck_level || ""} onChange={(e) => set("deck_level", e.target.value)} placeholder="e.g. Main Deck, 02 Level" />)}
          {field("Compartment / Room", <Input className={inputCls} value={editing.compartment || ""} onChange={(e) => set("compartment", e.target.value)} placeholder="e.g. Engine Room 2" />)}
          {field("Inner / Outer",
            <Select value={editing.inner_outer || ""} onValueChange={(v) => set("inner_outer", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{INNER_OUTER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="col-span-3">
            {field("Free-text location", <Input className={inputCls} value={editing.location_on_vessel || ""} onChange={(e) => set("location_on_vessel", e.target.value)} placeholder="Optional descriptive location" />)}
          </div>
          <div className="col-span-3 mt-2">
            <Label className="text-xs text-primary-foreground/60 uppercase tracking-wide">3D Position (for future model overlay)</Label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <Input type="number" step="any" className={inputCls} value={editing.pos_x ?? ""} onChange={(e) => set("pos_x", e.target.value === "" ? null : Number(e.target.value))} placeholder="X (m)" />
              <Input type="number" step="any" className={inputCls} value={editing.pos_y ?? ""} onChange={(e) => set("pos_y", e.target.value === "" ? null : Number(e.target.value))} placeholder="Y (m)" />
              <Input type="number" step="any" className={inputCls} value={editing.pos_z ?? ""} onChange={(e) => set("pos_z", e.target.value === "" ? null : Number(e.target.value))} placeholder="Z (m)" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="drawings" className="grid grid-cols-2 gap-4 pt-4">
          {field("GA Drawing No.", <Input className={inputCls} value={editing.ga_drawing_no || ""} onChange={(e) => set("ga_drawing_no", e.target.value)} placeholder="e.g. GA-001-Rev-C" />)}
          {field("P&ID Tag", <Input className={inputCls} value={editing.pid_tag || ""} onChange={(e) => set("pid_tag", e.target.value)} placeholder="e.g. P-ME-LO-001" />)}
          {field("Equipment Tag (KKS / ISO 14224)", <Input className={inputCls} value={editing.equipment_tag || ""} onChange={(e) => set("equipment_tag", e.target.value)} placeholder="e.g. =MKA10AP001" />)}
        </TabsContent>

        <TabsContent value="manufacturer" className="grid grid-cols-2 gap-4 pt-4">
          {field("Manufacturer", <Input className={inputCls} value={editing.manufacturer || ""} onChange={(e) => set("manufacturer", e.target.value)} />)}
          {field("Country", <Input className={inputCls} value={editing.manufacturer_country || ""} onChange={(e) => set("manufacturer_country", e.target.value)} />)}
          {field("Model", <Input className={inputCls} value={editing.model || ""} onChange={(e) => set("model", e.target.value)} />)}
          {field("Serial No.", <Input className={inputCls} value={editing.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} />)}
          {field("Manufacturer Contact", <Input className={inputCls} value={editing.manufacturer_contact || ""} onChange={(e) => set("manufacturer_contact", e.target.value)} placeholder="email / phone / portal" />)}
          {field("Install Date", <Input type="date" className={inputCls} value={editing.install_date || ""} onChange={(e) => set("install_date", e.target.value)} />)}
          {field("Commissioned", <Input type="date" className={inputCls} value={editing.commissioned_date || ""} onChange={(e) => set("commissioned_date", e.target.value)} />)}
          {field("Warranty Expiry", <Input type="date" className={inputCls} value={editing.warranty_expiry || ""} onChange={(e) => set("warranty_expiry", e.target.value)} />)}
          {field("Expected Life (years)", <Input type="number" className={inputCls} value={editing.expected_life_years ?? ""} onChange={(e) => set("expected_life_years", e.target.value === "" ? null : Number(e.target.value))} />)}
        </TabsContent>

        <TabsContent value="jobs" className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-primary-foreground/70">
              Recommended maintenance events for this component. Crew see these on the PMS calendar.
            </p>
            <Button size="sm" onClick={onAddJob}><Plus className="w-4 h-4 mr-1" />Add Job</Button>
          </div>
          {jobs.length === 0 ? (
            <p className="text-xs text-primary-foreground/40 py-8 text-center">No maintenance jobs yet.</p>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <div key={j.id} className="flex items-center gap-3 p-3 rounded border border-primary-foreground/10 bg-primary-foreground/3 hover:bg-primary-foreground/5">
                  <Wrench className="w-4 h-4 text-teal shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-primary-foreground/90 truncate">{j.title}</div>
                    <div className="text-[10px] text-primary-foreground/40 font-mono uppercase">
                      {j.job_number} · {j.job_type === "running_hours" ? `every ${j.interval_hours}h` : `every ${j.interval_days}d`} · {j.priority}
                      {j.class_requirement && " · CLASS"}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => onEditJob(j)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeleteJob(j)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function JobEditor({
  editing,
  setEditing,
  onSave,
  onCancel,
}: {
  editing: Partial<Job>;
  setEditing: (j: Partial<Job>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (k: keyof Job, v: any) => setEditing({ ...editing, [k]: v });
  const inputCls = "bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground";

  const steps: string[] = Array.isArray(editing.procedure_steps) ? editing.procedure_steps : [];
  const setSteps = (s: string[]) => set("procedure_steps", s);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-primary-foreground/10 pb-3">
        <h2 className="text-lg font-semibold text-primary-foreground flex items-center gap-2">
          <Wrench className="w-5 h-5 text-teal" />
          {editing.id ? "Edit Maintenance Job" : "New Maintenance Job"}
        </h2>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={onSave}><Save className="w-4 h-4 mr-1" />Save</Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-primary-foreground/5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="procedure">Procedure</TabsTrigger>
          <TabsTrigger value="safety">Safety & Resources</TabsTrigger>
          <TabsTrigger value="regulatory">Class & Sign-off</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="grid grid-cols-2 gap-4 pt-4">
          <div className="col-span-2">
            {field("Title *", <Input className={inputCls} value={editing.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Replace ME LO filter" />)}
          </div>
          <div className="col-span-2">
            {field("Description", <Textarea className={inputCls} rows={2} value={editing.description || ""} onChange={(e) => set("description", e.target.value)} />)}
          </div>
          {field("Job Type",
            <Select value={editing.job_type || "calendar"} onValueChange={(v) => set("job_type", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="calendar">Calendar (days)</SelectItem>
                <SelectItem value="running_hours">Running hours</SelectItem>
                <SelectItem value="condition">Condition-based</SelectItem>
                <SelectItem value="event">Event-based</SelectItem>
              </SelectContent>
            </Select>
          )}
          {editing.job_type === "running_hours"
            ? field("Interval (hours)", <Input type="number" className={inputCls} value={editing.interval_hours ?? ""} onChange={(e) => set("interval_hours", e.target.value === "" ? null : Number(e.target.value))} />)
            : field("Interval (days)", <Input type="number" className={inputCls} value={editing.interval_days ?? ""} onChange={(e) => set("interval_days", e.target.value === "" ? null : Number(e.target.value))} />)
          }
          {field("Priority",
            <Select value={editing.priority || "medium"} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          )}
          {field("Department",
            <Select value={editing.department || "engine"} onValueChange={(v) => set("department", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="engine">Engine</SelectItem>
                <SelectItem value="deck">Deck</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="electronics">Electronics</SelectItem>
                <SelectItem value="catering">Catering</SelectItem>
              </SelectContent>
            </Select>
          )}
          {field("Est. Duration (hours)", <Input type="number" step="0.25" className={inputCls} value={editing.estimated_duration_hours ?? ""} onChange={(e) => set("estimated_duration_hours", e.target.value === "" ? null : Number(e.target.value))} />)}
          {field("Required Rank/Rating", <Input className={inputCls} value={editing.required_rank || ""} onChange={(e) => set("required_rank", e.target.value)} placeholder="e.g. 2/E or above" />)}
          {field("Criticality",
            <Select value={editing.criticality || "B"} onValueChange={(v) => set("criticality", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>{CRITICALITIES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </TabsContent>

        <TabsContent value="procedure" className="space-y-4 pt-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-primary-foreground/60 uppercase tracking-wide">Step-by-step Procedure</Label>
              <Button size="sm" variant="outline" onClick={() => setSteps([...steps, ""])}>
                <Plus className="w-3.5 h-3.5 mr-1" />Add Step
              </Button>
            </div>
            <div className="space-y-2">
              {steps.length === 0 && <p className="text-xs text-primary-foreground/30">No steps yet.</p>}
              {steps.map((s, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs text-teal font-mono w-6 pt-2">{i + 1}.</span>
                  <Textarea
                    className={inputCls + " flex-1"}
                    rows={2}
                    value={s}
                    onChange={(e) => {
                      const n = [...steps];
                      n[i] = e.target.value;
                      setSteps(n);
                    }}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {field("Free-text Procedures (legacy / extra notes)", <Textarea className={inputCls} rows={3} value={editing.procedures || ""} onChange={(e) => set("procedures", e.target.value)} />)}
          {field("Acceptance Criteria", <Textarea className={inputCls} rows={2} value={editing.acceptance_criteria || ""} onChange={(e) => set("acceptance_criteria", e.target.value)} placeholder="e.g. Differential pressure < 0.5 bar; oil sample within ISO 4406 limits" />)}
        </TabsContent>

        <TabsContent value="safety" className="grid grid-cols-2 gap-4 pt-4">
          {field("Tools Required", <Textarea className={inputCls} rows={2} value={editing.tools_required || ""} onChange={(e) => set("tools_required", e.target.value)} />)}
          {field("Spares Required", <Textarea className={inputCls} rows={2} value={editing.spares_required || ""} onChange={(e) => set("spares_required", e.target.value)} placeholder="Part numbers + quantities" />)}
          {field("PPE Required", <Textarea className={inputCls} rows={2} value={editing.ppe_required || ""} onChange={(e) => set("ppe_required", e.target.value)} placeholder="e.g. Coveralls, gloves, eye protection" />)}
          {field("Isolations / LOTO", <Textarea className={inputCls} rows={2} value={editing.isolations_loto || ""} onChange={(e) => set("isolations_loto", e.target.value)} placeholder="Energy sources to isolate, lock-out points" />)}
          <div className="col-span-2">
            {field("Permits Required", <Input className={inputCls} value={editing.permits_required || ""} onChange={(e) => set("permits_required", e.target.value)} placeholder="e.g. Hot Work, Enclosed Space, Working Aloft" />)}
          </div>
        </TabsContent>

        <TabsContent value="regulatory" className="grid grid-cols-2 gap-4 pt-4">
          <div className="col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="class_req"
              checked={!!editing.class_requirement}
              onChange={(e) => set("class_requirement", e.target.checked)}
              className="accent-teal"
            />
            <Label htmlFor="class_req" className="text-sm text-primary-foreground/80">This is a Class survey item</Label>
          </div>
          {field("Class Society", <Input className={inputCls} value={editing.class_society || ""} onChange={(e) => set("class_society", e.target.value)} placeholder="ABS, DNV, LR, BV…" />)}
          {field("Regulatory Reference", <Input className={inputCls} value={editing.regulatory_reference || ""} onChange={(e) => set("regulatory_reference", e.target.value)} placeholder="e.g. SOLAS Ch II-1 / ABS Rule 4-6" />)}
          <div className="col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="cms"
              checked={!!editing.cms_credit}
              onChange={(e) => set("cms_credit", e.target.checked)}
              className="accent-teal"
            />
            <Label htmlFor="cms" className="text-sm text-primary-foreground/80">Eligible for CMS credit</Label>
          </div>
          <div className="col-span-2">
            {field("Sign-off Chain", <Input className={inputCls} value={editing.signoff_chain || ""} onChange={(e) => set("signoff_chain", e.target.value)} placeholder="e.g. Performer → 2/E → C/E → Master" />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
